import { Button } from "@/components/ui/button";
import { Phone, ArrowDown } from "lucide-react";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import heroBg from "@/assets/hero-bg.jpg";

export const Hero = () => {
  const [showPhone, setShowPhone] = useState(false);
  const [currentText, setCurrentText] = useState(0);
  const texts = ["Pracowników", "Gości", "Klientów"];

  useEffect(() => {
    const phoneInterval = setInterval(() => {
      setShowPhone(prev => !prev);
    }, 3000);
    const textInterval = setInterval(() => {
      setCurrentText(prev => (prev + 1) % texts.length);
    }, 4000);
    return () => {
      clearInterval(phoneInterval);
      clearInterval(textInterval);
    };
  }, []);

  const scrollToContactForm = () => {
    document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToAbout = () => {
    document.getElementById('o-nas')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center text-white overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img 
          src={heroBg} 
          alt="" 
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-[#2A2040]/70 to-[#1A1F2C]/80" />
      </div>

      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-[500px] h-[500px] bg-brand-purple/15 rounded-full -top-40 -left-40 blur-3xl animate-float" />
        <div className="absolute w-[600px] h-[600px] bg-brand-magenta/8 rounded-full -bottom-60 -right-40 blur-3xl animate-float-slow" />
      </div>

      <div className="container relative z-10 text-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-6 px-6 py-2 rounded-full border border-brand-purple/30 bg-brand-purple/10 backdrop-blur-sm"
          >
            <span className="text-brand-purple-light font-inter text-sm tracking-wider uppercase">
              🎭 Profesjonalna komedia dla firm
            </span>
          </motion.div>

          <h1 className="font-montserrat text-5xl md:text-7xl lg:text-8xl font-extrabold mb-8 leading-tight">
            Rozbaw swoich<br />
            <AnimatePresence mode="wait">
              <motion.span
                key={currentText}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="gradient-text inline-block"
              >
                {texts[currentText]}!
              </motion.span>
            </AnimatePresence>
          </h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="font-inter text-lg md:text-xl mb-12 max-w-2xl mx-auto text-gray-300/90 leading-relaxed"
          >
            <p>
              Profesjonalne występy komediowe dla firm i osób prywatnych,
              które łączą ludzi i budują pozytywną atmosferę ;-)
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button 
              size="lg" 
              onClick={scrollToContactForm}
              className="bg-brand-purple hover:bg-brand-purple-dark text-white text-lg w-64 py-6 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-brand-purple/30 hover:shadow-xl hover:shadow-brand-purple/40 transition-all duration-300 hover:scale-105"
            >
              {showPhone ? (
                <>
                  <Phone className="w-5 h-5" />
                  +48 533 333 993
                </>
              ) : (
                "Zamów występ"
              )}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={scrollToAbout}
              className="border-2 border-brand-purple/60 text-brand-purple hover:bg-brand-purple/10 hover:border-brand-purple text-lg px-10 py-6 rounded-full transition-all duration-300 hover:scale-105"
            >
              Dowiedz się więcej
            </Button>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="cursor-pointer"
            onClick={scrollToAbout}
          >
            <ArrowDown className="w-6 h-6 text-gray-400" />
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};
